/**
 * The tab's headline: the two things that must be true before money moves.
 *
 * WHY THIS EXISTS. The three endpoints on this tab each produce a net-wire
 * figure, by three different routes:
 *
 *   statement.netWireToTitle  = totalLoanAmount − Σ titleLines
 *                               (funding_statements.py FundingStatementBase)
 *   netWire.netWireToTitle    = initialAdvance + credits − fees − escrows
 *                               − financedInterestReserve   (NetWireCalculator)
 *   wireInstructions.wireAmount = what the closer typed on the ticket
 *
 * They are computed independently and they must agree. Nothing in the product
 * checked that — you had to read three cards and compare by eye. Gate A does it.
 *
 * Gate B is the backend's own tie-out: `hudBalanceVariance = hudSourcesTotal −
 * hudUsesTotal`, which must be exactly zero — no tolerance. Red unless
 * balanced is the whole point — it is the closer's "does this wire balance?"
 * light.
 *
 * Gate C is the fee tie-out: `lenderChargesVariance = Σ hudLenderCharges −
 * lenderWithheldTotal`. The HUD balance cannot see a fee difference (the wire
 * plus what we withheld always adds back to the gross loan), so this is its own
 * zero-tolerance check (funding-statements.md, 2026-09-29).
 *
 * All gates are pure derivations of payloads the tab shell already fetched; the
 * bar never fetches and never writes.
 */
import PropTypes from "prop-types";

import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";

import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { useDrilldownBaseUrl } from "../../../../pipelines/PipelineContext";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import {
  settlementTypeLabel,
  useResolveLoanDocument,
} from "./panes/HudSourceChip";
import { Pill, T, money, signedMoney } from "./wireFundingShared";

/**
 * Zero tolerance: the figures either tie out to the penny or they do not. There
 * is no rounding allowance on a wire — a cent of drift is a real difference.
 */
const TOLERANCE = 0;

const toNum = (v) => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Gate A state across however many of the three figures actually exist.
 * "pending" (not "mismatch") when a figure is simply absent — an unfilled wire
 * ticket is normal early in the flow and must not read as an error.
 */
export const wireAgreement = ({ statement, netWire, wireInstructions }) => {
  const figures = [
    { key: "statement", label: "Statement", value: toNum(statement?.netWireToTitle) },
    { key: "calculator", label: "Calculator", value: toNum(netWire?.netWireToTitle) },
    { key: "ticket", label: "Wire ticket", value: toNum(wireInstructions?.wireAmount) },
  ];
  const present = figures.filter((f) => f.value != null);

  if (present.length < 2) {
    return { figures, state: "pending", spread: null, present: present.length };
  }
  const values = present.map((f) => f.value);
  const spread = Math.max(...values) - Math.min(...values);
  const agrees = spread <= TOLERANCE;

  return {
    figures,
    state: agrees ? (present.length === 3 ? "agree" : "partial") : "mismatch",
    spread,
    present: present.length,
  };
};

/**
 * Gate B — the backend's HUD variance.
 *
 * `noHud`: the title side is PREPARED from loan fields (no extracted HUD), so
 * the backend sends no variance at all — "No HUD to compare", not "no lines"
 * (the prepared lines are on screen) and never "out of balance"
 * (funding-statements.md, closing assessment answer 5).
 */
export const hudTieOut = (netWire) => {
  const variance = toNum(netWire?.hudBalanceVariance);
  if (variance == null) {
    return {
      state: "pending",
      variance: null,
      noHud: netWire?.hudCompareSource === "prepared",
    };
  }
  return {
    state: Math.abs(variance) <= TOLERANCE ? "balanced" : "off",
    variance,
  };
};

/**
 * Gate C — the lender-charges (fee) tie-out.
 *
 * `none` when no HUD line names our lender (`lenderChargesVariance` is null):
 * there is nothing to tie out, and that is NOT a pass. Positive = the HUD
 * charges the borrower more for the lender than the LOS withheld; negative =
 * the LOS withholds something the HUD doesn't charge.
 */
export const lenderChargesTieOut = (netWire) => {
  const variance = toNum(netWire?.lenderChargesVariance);
  const noHud = netWire?.hudCompareSource === "prepared";
  if (variance == null) return { state: "none", variance: null, noHud };
  return {
    state: Math.abs(variance) <= TOLERANCE ? "tied" : "off",
    variance,
    noHud,
  };
};

/** The sentence that explains a non-zero lender-charges variance. */
export const lenderChargesMeaning = (variance) =>
  variance > 0
    ? "The HUD charges the borrower more for the lender than the LOS withheld from the wire — a lender line on the HUD that is not on the loan terms."
    : "The LOS withholds something from the wire that the HUD does not charge the borrower for the lender.";

/**
 * One check, as a chip that TAKES YOU TO THE PANE THAT FIXES IT.
 *
 * That is the whole reason this is a button and not a read-out: a failing check
 * is a piece of work, and the closer's next move is always "go look at the thing
 * that's wrong". Passing checks stay visible in green rather than disappearing —
 * a check you can't see is a check you can't trust.
 */
const CheckChip = ({ label, figure, passing, onClick }) => (
  <Flx
    ac
    gap={1}
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick();
      }
    }}
    sx={{
      px: 1.25,
      py: 0.75,
      cursor: "pointer",
      borderRadius: T.radiusSm,
      border: `1px solid ${passing ? "#CBE8D6" : "#F2CCCC"}`,
      bgcolor: passing ? T.greenTint : T.redTint,
      transition: "filter .15s",
      "&:hover": { filter: "brightness(.985)" },
      "&:hover .chk-arrow": { transform: "translateX(2px)" },
      "&:focus-visible": {
        outline: `2px solid ${T.accent}`,
        outlineOffset: 2,
      },
    }}
  >
    <Txt sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>{label}</Txt>
    <Txt
      sx={{
        fontFamily: T.mono,
        fontSize: 12.5,
        fontWeight: 600,
        color: passing ? T.green : T.red,
      }}
    >
      {figure}
    </Txt>
    <ArrowForwardRounded
      className="chk-arrow"
      sx={{ fontSize: 15, color: T.faint, transition: "transform .15s" }}
    />
  </Flx>
);

const WireReconciliationBar = ({ statement, netWire, wireInstructions }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const resolveDoc = useResolveLoanDocument();
  const { id: dealId } = useParams();
  // Pipeline-aware base so acquisitions loans stay under /acquisition/:id.
  const baseUrl = useDrilldownBaseUrl(dealId);

  const agreement = wireAgreement({ statement, netWire, wireInstructions });
  const tieOut = hudTieOut(netWire);
  const lender = lenderChargesTieOut(netWire);

  const goTo = (pane) => navigate(`${baseUrl}/wire-and-funding/${pane}`);

  const agreementPassing = agreement.state === "agree";
  const tieOutPassing = tieOut.state === "balanced";

  // "Pending" is not "failing" — an unfilled wire ticket or a HUD with no lines
  // yet is the normal early state, and counting it as a failure would cry wolf.
  const failing =
    (agreement.state === "mismatch" ? 1 : 0) +
    (tieOut.state === "off" ? 1 : 0) +
    (lender.state === "off" ? 1 : 0);

  const agreementFigure =
    agreement.state === "mismatch"
      ? `off by ${money(agreement.spread)}`
      : agreement.state === "agree"
        ? "in agreement"
        : `${agreement.present} of 3 entered`;

  const tieOutFigure =
    tieOut.state === "off"
      ? money(tieOut.variance)
      : tieOut.state === "balanced"
        ? "balanced"
        : tieOut.noHud
          ? "no HUD to compare"
          : "no HUD lines";

  const lenderFigure =
    lender.state === "off"
      ? signedMoney(lender.variance)
      : lender.state === "tied"
        ? "tied out"
        : lender.noHud
          ? "no HUD to compare"
          : "no lender line on HUD";

  return (
    <Flx
      ac
      wrap
      gap={1.25}
      sx={{
        bgcolor: T.card,
        border: `1px solid ${failing ? "#F2CCCC" : T.line}`,
        borderLeft: `4px solid ${failing ? T.red : T.green}`,
        borderRadius: T.radius,
        boxShadow: T.shadow,
        px: 2,
        py: 1,
        mb: 2,
      }}
    >
      <Flx ac gap={1}>
        <Box
          component="span"
          sx={{
            fontSize: 11.5,
            fontWeight: 700,
            px: 1,
            py: 0.25,
            borderRadius: "999px",
            bgcolor: failing ? T.redTint : T.greenTint,
            color: failing ? T.red : T.green,
            border: `1px solid ${failing ? "#F2CCCC" : "#CBE8D6"}`,
          }}
        >
          {failing ? `${failing} failing` : "All clear"}
        </Box>
        <Txt sx={{ fontSize: 13, fontWeight: 700, color: T.ink }}>
          Balance checks
        </Txt>
      </Flx>

      <CheckChip
        label="Net wire agreement"
        figure={agreementFigure}
        passing={agreementPassing}
        onClick={() => goTo("wire-ticket")}
      />
      <CheckChip
        label="HUD tie-out"
        figure={tieOutFigure}
        passing={tieOutPassing}
        onClick={() => goTo("tie-out")}
      />
      <CheckChip
        label="Lender charges"
        figure={lenderFigure}
        passing={lender.state === "tied"}
        onClick={() => goTo("tie-out")}
      />

      <Box sx={{ flex: 1 }} />

      {/* Which HUD the balance is against (funding-statements.md
          2026-10-05). Falls back to the old extract / prepared word while the
          backend does not send hudSource yet. */}
      {netWire?.hudSource ? (
        <Flx ac gap={0.75} sx={{ minWidth: 0, maxWidth: "100%" }}>
          <Txt sx={{ fontSize: 11.5, color: T.faint, flex: "none" }}>
            Balanced against
          </Txt>
          <ButtonBase
            onClick={() => {
              const doc = resolveDoc(netWire.hudSource);
              if (doc) dispatch(filePreviewSetData(doc));
            }}
            disabled={!resolveDoc(netWire.hudSource)}
            title={netWire.hudSource.fileName || ""}
            sx={{
              fontSize: 11.5,
              fontWeight: 600,
              color: T.accentInk,
              maxWidth: 260,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              display: "block",
              "&:hover": { textDecoration: "underline" },
              "&.Mui-disabled": { color: T.ink2 },
            }}
          >
            {netWire.hudSource.fileName ||
              netWire.hudSource.fileStorageName ||
              "settlement statement"}
          </ButtonBase>
          {settlementTypeLabel(netWire.hudSource) ? (
            <Pill label={settlementTypeLabel(netWire.hudSource)} tone="info" />
          ) : null}
          {netWire.hudSource.isExecuted === false ? (
            <Pill label="Draft" tone="warning" />
          ) : null}
          {netWire.hudNewerVersions?.length ? (
            <ButtonBase onClick={() => goTo("tie-out")} sx={{ borderRadius: "5px" }}>
              <Pill label="Newer version on file" tone="warning" />
            </ButtonBase>
          ) : null}
        </Flx>
      ) : netWire?.hudCompareSource ? (
        <Txt sx={{ fontSize: 11.5, color: T.faint }}>
          {netWire.hudCompareSource === "prepared" ? "Prepared" : "HUD extract"}
        </Txt>
      ) : null}
    </Flx>
  );
};

WireReconciliationBar.propTypes = {
  statement: PropTypes.object,
  netWire: PropTypes.object,
  wireInstructions: PropTypes.object,
};

export default WireReconciliationBar;